import { useState } from "react";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { InvoiceBankAccount, PaymentMethod, Seller } from "@/services/invoiceGeneration";
import { BankgiroEntryForm } from "./BankgiroEntryForm";

/**
 * Create Invoice's Payment method field (FIN-818, Draft B). Choosing Bankgiro when there is none
 * opens an inline form rather than being refused. A new bankgiro is saved by number alone, with no
 * account (FIN-1172); one already typed in on the receiving account is changed in place.
 */
export function PaymentMethodField({ seller, value, onChange, onSaveBankgiro, onSaveCompanyBankgiro }: {
  seller: Seller | null;
  /** The invoice's own choice; unset means whatever the seller resolves to. */
  value: PaymentMethod | null;
  onChange: (method: PaymentMethod) => void;
  /** Changes the bankgiro typed in on that account and resolves with the refreshed seller. */
  onSaveBankgiro: (bankAccountId: string, typed: string) => Promise<Seller>;
  /** Stores the company's bankgiro by number alone and resolves with the refreshed seller. */
  onSaveCompanyBankgiro: (typed: string) => Promise<Seller>;
}) {
  const t = useTranslations("consultantInvoiceCreate.paymentMethod");
  // accountId null is the company bankgiro.
  const [adding, setAdding] = useState<{ accountId: string | null; initial: string } | null>(null);
  const method = value ?? seller?.resolvedPaymentMethod ?? "bank_account";
  const receiving = seller?.invoiceBankAccounts.find((a) => a.receivesInvoices) ?? null;
  const hasBankgiro = !!seller?.bankgiroAvailable;
  const shown = shownBankgiro(seller, receiving);

  const onSelect = (method: string) => {
    const wantsMissingBankgiro = method === "bankgiro" && !hasBankgiro;
    setAdding(wantsMissingBankgiro ? { accountId: null, initial: "" } : null);
    if (!wantsMissingBankgiro) onChange(method as PaymentMethod);
  };

  return (
    <div>
      <Label className="mb-1.5 block">{t("label")}</Label>
      <Select value={adding ? "bankgiro" : method} onValueChange={onSelect}>
        <SelectTrigger aria-label={t("label")}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="bankgiro">
            {t("bankgiro")}{!hasBankgiro && <span className="ml-2 text-xs text-muted-foreground">{t("addNumber")}</span>}
          </SelectItem>
          <SelectItem value="bank_account">{t("bankAccount")}</SelectItem>
        </SelectContent>
      </Select>

      {adding && (
        <BankgiroEntryForm
          initialValue={adding.initial}
          onCancel={() => setAdding(null)}
          onSave={async (typed) => {
            const updated = adding.accountId ? await onSaveBankgiro(adding.accountId, typed) : await onSaveCompanyBankgiro(typed);
            setAdding(null);
            onChange("bankgiro");
            return updated;
          }}
        />
      )}

      {!adding && method === "bankgiro" && shown && (
        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {t("bankgiroNumber", { number: shown.number })}
          <Badge variant={shown.provenance === "manual" ? "outline" : "secondary"}>
            {shown.provenance === "manual" ? t("addedByYou") : t("fromYourBank")}
          </Badge>
          {shown.provenance === "manual" && (
            <Button
              type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
              onClick={() => setAdding({ accountId: shown.accountId, initial: shown.number })}
            >
              {t("change")}
            </Button>
          )}
        </p>
      )}
    </div>
  );
}

/** The receiving account's own bankgiro wins; otherwise the company's (the backend's order). */
function shownBankgiro(seller: Seller | null, receiving: InvoiceBankAccount | null) {
  if (receiving?.bankgiro) {
    return { number: receiving.bankgiro, provenance: receiving.bankgiroProvenance, accountId: receiving.id as string | null };
  }
  if (seller?.companyBankgiro) return { number: seller.companyBankgiro, provenance: "manual" as const, accountId: null };
  return null;
}
